import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Zap, ChevronDown, ChevronUp, Check, ExternalLink, Copy, X, Loader2,
  Target, Star, Globe, MessageSquare, RefreshCw, Shield, FileText, Award,
  ArrowRight, Link2, CheckCircle2, Clock, Sparkles,
} from 'lucide-react';
import axios from 'axios';
import AppShell from '../components/AppShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getToken() {
  return document.cookie.split('; ').find(r => r.startsWith('session_token='))?.split('=')[1]
    || localStorage.getItem('session_token') || '';
}
const headers = () => ({ Authorization: `Bearer ${getToken()}` });

const SOURCE_ICONS = {
  content: Sparkles,
  directories: Globe,
  reviews: Star,
  freshness: RefreshCw,
  consistency: Shield,
  entity: Target,
};

const EFFORT_LABELS = {
  quick: { label: '~5 min', color: '#1D6B4F' },
  medium: { label: '~15 min', color: '#B8985A' },
  intensive: { label: '~30+ min', color: '#C67A5C' },
};

const CONFIRM_PLATFORMS = [
  { id: 'website', label: 'My Website' },
  { id: 'google_business', label: 'Google Business Profile' },
  { id: 'yelp', label: 'Yelp' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'reddit', label: 'Reddit' },
  { id: 'nextdoor', label: 'Nextdoor' },
  { id: 'quora', label: 'Quora' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'chamber', label: 'Chamber of Commerce' },
  { id: 'other', label: 'Other' },
];

export default function ActionCenter() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [generating, setGenerating] = useState(null);
  const [generatedContent, setGeneratedContent] = useState({});
  const [showPortfolio, setShowPortfolio] = useState(false);

  const fetchActions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/action-center`, { headers: headers() });
      setData(res.data);
    } catch (e) {
      if (e.response?.status === 401) navigate('/login');
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { fetchActions(); }, [fetchActions]);

  const handleStatusUpdate = async (actionId, newStatus) => {
    try {
      await axios.put(`${API}/action-center/${actionId}/status`, { status: newStatus }, { headers: headers() });
      fetchActions();
    } catch (e) { console.error(e); }
  };

  const handleGenerateContent = async (actionId) => {
    setGenerating(actionId);
    try {
      const res = await axios.post(`${API}/action-center/${actionId}/generate-content`, {}, { headers: headers() });
      setGeneratedContent(prev => ({ ...prev, [actionId]: res.data.content }));
    } catch (e) { console.error(e); }
    setGenerating(null);
  };

  if (loading) {
    return (
      <AppShell active="actions">
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FDFBF7' }}>
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4C9B8', borderTopColor: 'transparent' }} />
        </div>
      </AppShell>
    );
  }

  if (!data?.has_business) {
    return (
      <AppShell active="actions">
        <div className="min-h-screen flex flex-col items-center justify-center px-6" style={{ backgroundColor: '#FDFBF7' }}>
          <Target size={32} strokeWidth={1.5} style={{ color: '#B8985A' }} className="mb-4" />
          <h2 className="text-xl font-light mb-2" style={{ color: '#3C3226' }}>Action Center</h2>
          <p className="text-sm text-center mb-6" style={{ color: '#8C7E6E' }}>Complete your business profile to get your personalized action plan.</p>
          <button onClick={() => navigate('/onboarding')} className="px-6 py-3 text-[12px] font-medium uppercase tracking-wider" style={{ backgroundColor: '#2C2520', color: '#F0EBE3' }} data-testid="start-onboarding-btn">
            Begin monitoring
          </button>
        </div>
      </AppShell>
    );
  }

  const { actions, stats, sources } = data;
  const activeActions = data.active_actions || [];
  const completedActions = data.completed_actions || [];
  const weekProgress = stats.week_total > 0 ? Math.round((stats.week_completed / stats.week_total) * 100) : 0;

  return (
    <AppShell active="actions">
      <div className="min-h-screen pb-24 desktop:pb-8" style={{ backgroundColor: '#FDFBF7' }} data-testid="action-center-page">
        <header className="px-6 pt-6 pb-2">
          <p className="text-[11px] uppercase tracking-widest mb-2" style={{ color: '#B8985A' }}>AI Presence Builder</p>
          <h1 className="text-[24px] font-light" style={{ color: '#3C3226', fontFamily: "'DM Sans', sans-serif" }} data-testid="action-center-title">Action Center</h1>
          <p className="text-[13px] mt-1" style={{ color: '#8C7E6E' }}>Your personalized tasks to strengthen how AI mentions your business.</p>
        </header>

        <main className="px-6 max-w-lg mx-auto tablet:max-w-2xl desktop:max-w-5xl">
          {/* Week Progress */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="p-5 mb-4" style={{ backgroundColor: '#2C2520' }} data-testid="week-progress-card">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] uppercase tracking-widest" style={{ color: '#B8985A' }}>This Week</p>
              <span className="text-[11px]" style={{ color: '#F0EBE3' }}>{stats.week_completed}/{stats.week_total} completed</span>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden mb-3" style={{ backgroundColor: '#4A3F36' }}>
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${weekProgress}%`, backgroundColor: '#1D6B4F' }} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <p className="text-[20px] font-light" style={{ color: '#F0EBE3' }}>{stats.total_tasks}</p>
                <p className="text-[10px] uppercase tracking-wider" style={{ color: '#8C7E6E' }}>Total Tasks</p>
              </div>
              <div>
                <p className="text-[20px] font-light" style={{ color: '#1D6B4F' }}>{stats.completed}</p>
                <p className="text-[10px] uppercase tracking-wider" style={{ color: '#8C7E6E' }}>Completed</p>
              </div>
              <div>
                <p className="text-[20px] font-light" style={{ color: '#B8985A' }}>+{stats.total_impact_earned}</p>
                <p className="text-[10px] uppercase tracking-wider" style={{ color: '#8C7E6E' }}>Impact Earned</p>
              </div>
            </div>
          </motion.div>

          {/* Portfolio Link */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="grid grid-cols-2 gap-3 mb-4">
            <button
              onClick={() => setShowPortfolio(true)}
              className="p-4 flex items-center gap-3 transition-colors hover:bg-[#EDE8DF] text-left"
              style={{ border: '1px solid #D4C9B8' }}
              data-testid="portfolio-btn"
            >
              <Award size={16} strokeWidth={1.5} style={{ color: '#B8985A' }} />
              <div className="min-w-0">
                <span className="text-[12px] font-medium block" style={{ color: '#3C3226' }}>AI Presence Portfolio</span>
                <span className="text-[10px]" style={{ color: '#8C7E6E' }}>{stats.completed} completed</span>
              </div>
            </button>
            <button
              onClick={() => navigate('/presence')}
              className="p-4 flex items-center gap-3 transition-colors hover:bg-[#EDE8DF] text-left"
              style={{ border: '1px solid #D4C9B8' }}
              data-testid="presence-tools-btn"
            >
              <Shield size={16} strokeWidth={1.5} style={{ color: '#1D6B4F' }} />
              <div className="min-w-0">
                <span className="text-[12px] font-medium block" style={{ color: '#3C3226' }}>Presence Tools</span>
                <span className="text-[10px]" style={{ color: '#8C7E6E' }}>Consistency, freshness, guides</span>
              </div>
            </button>
          </motion.div>

          {/* Active Tasks */}
          {activeActions.length > 0 && (
            <div className="mb-6" data-testid="active-tasks">
              <h2 className="text-[11px] font-medium uppercase tracking-wider mb-3" style={{ color: '#8C7E6E' }}>To Do</h2>
              <div className="space-y-2">
                {activeActions.map((action, i) => (
                  <ActionCard
                    key={action.action_id}
                    action={action}
                    index={i}
                    sources={sources}
                    expanded={expandedId === action.action_id}
                    onToggle={() => setExpandedId(expandedId === action.action_id ? null : action.action_id)}
                    onStatusUpdate={handleStatusUpdate}
                    onConfirm={() => setConfirmModal(action)}
                    onGenerateContent={handleGenerateContent}
                    generating={generating === action.action_id}
                    generatedContent={generatedContent[action.action_id]}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Week Complete */}
          {activeActions.length === 0 && stats.week_total > 0 && (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="p-8 text-center mb-6" style={{ backgroundColor: '#F7F4EF', border: '1px solid #D4C9B8' }} data-testid="week-complete">
              <CheckCircle2 size={32} style={{ color: '#1D6B4F', margin: '0 auto 12px' }} />
              <h3 className="text-lg font-light mb-1" style={{ color: '#3C3226' }}>Week complete!</h3>
              <p className="text-sm" style={{ color: '#8C7E6E' }}>You've completed all your tasks. Your score will update as AI engines process the changes.</p>
            </motion.div>
          )}

          {/* Completed Tasks */}
          {completedActions.length > 0 && (
            <div className="mb-6" data-testid="completed-tasks">
              <h2 className="text-[11px] font-medium uppercase tracking-wider mb-3" style={{ color: '#1D6B4F' }}>Completed</h2>
              <div className="space-y-2">
                {completedActions.slice(0, 5).map((action, i) => {
                  const SourceIcon = SOURCE_ICONS[action.source] || Target;
                  const sourceInfo = sources[action.source] || {};
                  return (
                    <motion.div
                      key={action.action_id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="p-4 flex items-center gap-3"
                      style={{ backgroundColor: '#F7F4EF', border: '1px solid #E8E2D8', opacity: 0.7 }}
                      data-testid={`completed-${action.action_id}`}
                    >
                      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(29,107,79,0.06)' }}>
                        <Check size={14} style={{ color: '#1D6B4F' }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] line-through" style={{ color: '#8C7E6E' }}>{action.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] px-1.5 py-0.5" style={{ backgroundColor: `${sourceInfo.color || '#999'}15`, color: sourceInfo.color || '#999' }}>
                            {sourceInfo.label || action.source}
                          </span>
                          <span className="text-[10px]" style={{ color: '#8C7E6E' }}>{action.completed_at?.slice(0, 10)}</span>
                        </div>
                      </div>
                      <span className="text-[12px] font-medium" style={{ color: '#1D6B4F' }}>+{action.impact_points}</span>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Empty State */}
          {actions.length === 0 && (
            <div className="p-12 text-center" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8' }}>
              <Target size={32} style={{ color: '#B8985A', margin: '0 auto 16px' }} />
              <h3 className="text-lg font-light mb-2" style={{ color: '#3C3226' }}>Your Action Center is being prepared</h3>
              <p className="text-sm mb-4" style={{ color: '#8C7E6E' }}>We're analyzing your AI presence to generate your first batch of personalized tasks.</p>
            </div>
          )}
        </main>

        {/* Confirm Modal */}
        <AnimatePresence>
          {confirmModal && (
            <ConfirmModal
              action={confirmModal}
              onClose={() => setConfirmModal(null)}
              onConfirmed={() => { setConfirmModal(null); fetchActions(); }}
            />
          )}
        </AnimatePresence>

        {/* Portfolio Modal */}
        <AnimatePresence>
          {showPortfolio && (
            <PortfolioModal onClose={() => setShowPortfolio(false)} />
          )}
        </AnimatePresence>
      </div>
    </AppShell>
  );
}


/* ══════════════════════════════════════════
   ACTION CARD — Expandable task with Action Loop
   ══════════════════════════════════════════ */
function ActionCard({ action, index, sources, expanded, onToggle, onStatusUpdate, onConfirm, onGenerateContent, generating, generatedContent }) {
  const SourceIcon = SOURCE_ICONS[action.source] || Target;
  const sourceInfo = sources[action.source] || {};
  const effortInfo = EFFORT_LABELS[action.effort] || EFFORT_LABELS.medium;
  const loop = action.action_loop || {};
  const existingContent = loop.create?.generated_content || generatedContent;
  const [copied, setCopied] = useState(false);

  const copyContent = () => {
    if (existingContent) {
      navigator.clipboard.writeText(existingContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }}
      style={{ backgroundColor: '#FDFBF7', border: `1px solid ${expanded ? '#B8985A' : '#D4C9B8'}` }}
      data-testid={`action-card-${action.action_id}`}
    >
      {/* Card Header */}
      <button onClick={onToggle} className="w-full p-4 flex items-start gap-3 text-left" data-testid={`action-toggle-${action.action_id}`}>
        <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: `${sourceInfo.color || '#999'}10` }}>
          <SourceIcon size={18} strokeWidth={1.5} style={{ color: sourceInfo.color || '#999' }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-medium" style={{ color: '#3C3226' }}>{action.title}</p>
          <p className="text-[12px] mt-0.5" style={{ color: '#8C7E6E' }}>{action.description}</p>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-[10px] px-1.5 py-0.5 font-medium" style={{ backgroundColor: '#1D6B4F15', color: '#1D6B4F' }}>
              +{action.impact_points} pts
            </span>
            <span className="text-[10px] px-1.5 py-0.5" style={{ backgroundColor: `${sourceInfo.color || '#999'}15`, color: sourceInfo.color || '#999' }}>
              {sourceInfo.label || action.source}
            </span>
            <span className="text-[10px] flex items-center gap-1" style={{ color: effortInfo.color }}>
              <Clock size={9} /> {effortInfo.label}
            </span>
          </div>
        </div>
        <div className="flex-shrink-0 mt-1">
          {expanded ? <ChevronUp size={16} style={{ color: '#8C7E6E' }} /> : <ChevronDown size={16} style={{ color: '#8C7E6E' }} />}
        </div>
      </button>

      {/* Expanded Action Loop */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 space-y-4" style={{ borderTop: '1px solid #E8E2D8' }}>
              {/* Step 1: CREATE */}
              <div className="pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-5 h-5 flex items-center justify-center text-[10px] font-semibold" style={{ backgroundColor: '#B8985A', color: '#fff' }}>1</span>
                  <span className="text-[11px] font-medium uppercase tracking-wider" style={{ color: '#3C3226' }}>Create — We generate it</span>
                </div>
                {existingContent ? (
                  <div className="p-3" style={{ backgroundColor: '#F7F4EF', border: '1px solid #E8E2D8' }}>
                    <p className="text-[12px] whitespace-pre-wrap leading-relaxed" style={{ color: '#3C3226' }}>{existingContent}</p>
                    <button onClick={copyContent} className="mt-2 flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider" style={{ color: copied ? '#1D6B4F' : '#B8985A' }} data-testid={`copy-content-${action.action_id}`}>
                      {copied ? <><Check size={10} /> Copied</> : <><Copy size={10} /> Copy content</>}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => onGenerateContent(action.action_id)}
                    disabled={generating}
                    className="w-full p-3 text-[12px] font-medium flex items-center justify-center gap-2 transition-opacity hover:opacity-90 disabled:opacity-50"
                    style={{ backgroundColor: '#2C2520', color: '#F0EBE3' }}
                    data-testid={`generate-content-${action.action_id}`}
                  >
                    {generating ? <><Loader2 size={12} className="animate-spin" /> Generating...</> : <><Sparkles size={12} /> Generate Content</>}
                  </button>
                )}
              </div>

              {/* Step 2: GUIDE */}
              {loop.guide && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-5 h-5 flex items-center justify-center text-[10px] font-semibold" style={{ backgroundColor: '#B8985A', color: '#fff' }}>2</span>
                    <span className="text-[11px] font-medium uppercase tracking-wider" style={{ color: '#3C3226' }}>Guide — Where to post</span>
                  </div>
                  <div className="p-3" style={{ backgroundColor: '#F7F4EF', border: '1px solid #E8E2D8' }}>
                    <p className="text-[12px] font-medium" style={{ color: '#3C3226' }}>{loop.guide.platform}</p>
                    <p className="text-[11px] mt-1" style={{ color: '#5C5347' }}>{loop.guide.instructions}</p>
                    {loop.guide.url && (
                      <a href={loop.guide.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-2 text-[11px] font-medium" style={{ color: '#B8985A' }} data-testid={`guide-link-${action.action_id}`}>
                        <ExternalLink size={10} /> Open {loop.guide.platform}
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Step 3: DO */}
              {loop.do_steps?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-5 h-5 flex items-center justify-center text-[10px] font-semibold" style={{ backgroundColor: '#B8985A', color: '#fff' }}>3</span>
                    <span className="text-[11px] font-medium uppercase tracking-wider" style={{ color: '#3C3226' }}>Do — Step by step</span>
                  </div>
                  <div className="space-y-1.5">
                    {loop.do_steps.map(step => (
                      <div key={step.step} className="flex items-start gap-2 p-2" style={{ backgroundColor: '#F7F4EF' }}>
                        <span className="text-[10px] font-semibold mt-0.5 flex-shrink-0" style={{ color: '#B8985A' }}>{step.step}.</span>
                        <p className="text-[12px]" style={{ color: '#3C3226' }}>{step.instruction}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 4: CONFIRM */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-5 h-5 flex items-center justify-center text-[10px] font-semibold" style={{ backgroundColor: '#B8985A', color: '#fff' }}>4</span>
                  <span className="text-[11px] font-medium uppercase tracking-wider" style={{ color: '#3C3226' }}>Confirm — Mark it done</span>
                </div>
                <button
                  onClick={() => onConfirm(action)}
                  className="w-full p-3 text-[12px] font-medium uppercase tracking-wider flex items-center justify-center gap-2 transition-opacity hover:opacity-90"
                  style={{ backgroundColor: '#1D6B4F', color: '#F0EBE3' }}
                  data-testid={`confirm-btn-${action.action_id}`}
                >
                  <Check size={14} /> I Did This
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}


/* ══════════════════════════════════════════
   CONFIRM MODAL — "I Did This" flow
   ══════════════════════════════════════════ */
function ConfirmModal({ action, onClose, onConfirmed }) {
  const [platform, setPlatform] = useState('');
  const [url, setUrl] = useState('');
  const [saving, setSaving] = useState(false);

  const handleConfirm = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/action-center/${action.action_id}/confirm`, { platform, url }, { headers: headers() });
      onConfirmed();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
        className="w-full max-w-md mx-4 p-6"
        style={{ backgroundColor: '#FDFBF7', border: '1px solid #DDD5C8' }}
        onClick={e => e.stopPropagation()}
        data-testid="confirm-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium uppercase tracking-wider" style={{ color: '#3C3226' }}>Confirm Completion</h3>
          <button onClick={onClose} style={{ color: '#8C7E6E' }}><X size={18} /></button>
        </div>

        <p className="text-[13px] mb-4" style={{ color: '#5C5347' }}>{action.title}</p>

        <div className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: '#8C7E6E' }}>Where did you post/submit?</label>
            <select value={platform} onChange={e => setPlatform(e.target.value)} className="w-full px-3 py-2.5 text-sm outline-none appearance-none" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8', color: '#3C3226' }} data-testid="confirm-platform-select">
              <option value="">Select platform...</option>
              {CONFIRM_PLATFORMS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: '#8C7E6E' }}>Paste the URL (optional)</label>
            <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://..." className="w-full px-3 py-2.5 text-sm outline-none" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8', color: '#3C3226' }} data-testid="confirm-url-input" />
          </div>
          <button
            onClick={handleConfirm}
            disabled={saving}
            className="w-full py-3 text-[12px] font-medium uppercase tracking-wider transition-opacity hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ backgroundColor: '#1D6B4F', color: '#F0EBE3' }}
            data-testid="confirm-submit-btn"
          >
            {saving ? <><Loader2 size={12} className="animate-spin" /> Confirming...</> : <><CheckCircle2 size={14} /> Confirm</>}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}


/* ══════════════════════════════════════════
   PORTFOLIO MODAL — AI Presence Portfolio
   ══════════════════════════════════════════ */
function PortfolioModal({ onClose }) {
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/action-center/portfolio/summary`, { headers: headers() })
      .then(r => setPortfolio(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
        className="w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto"
        style={{ backgroundColor: '#FDFBF7', border: '1px solid #DDD5C8' }}
        onClick={e => e.stopPropagation()}
        data-testid="portfolio-modal"
      >
        <div className="p-5 flex items-center justify-between" style={{ borderBottom: '1px solid #E8E2D8' }}>
          <div>
            <p className="text-[11px] uppercase tracking-widest" style={{ color: '#B8985A' }}>AI Presence</p>
            <h3 className="text-base font-medium" style={{ color: '#3C3226' }}>Your Portfolio</h3>
          </div>
          <button onClick={onClose} style={{ color: '#8C7E6E' }}><X size={18} /></button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4C9B8', borderTopColor: 'transparent' }} />
          </div>
        ) : portfolio ? (
          <div className="p-5 space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Tasks Completed', value: portfolio.total_completed, color: '#3C3226' },
                { label: 'Platforms Active', value: portfolio.platforms_active, color: '#1D6B4F' },
                { label: 'Articles Published', value: portfolio.articles_published, color: '#B8985A' },
                { label: 'Impact Points', value: `+${portfolio.total_impact_points}`, color: '#C67A5C' },
              ].map(s => (
                <div key={s.label} className="p-3" style={{ backgroundColor: '#F7F4EF', border: '1px solid #E8E2D8' }}>
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: '#8C7E6E' }}>{s.label}</p>
                  <p className="text-xl font-light mt-1" style={{ color: s.color }}>{s.value}</p>
                </div>
              ))}
            </div>

            {/* Source Breakdown */}
            {Object.keys(portfolio.source_breakdown || {}).length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: '#8C7E6E' }}>By Category</p>
                <div className="space-y-1.5">
                  {Object.entries(portfolio.source_breakdown).map(([src, count]) => (
                    <div key={src} className="flex items-center justify-between p-2" style={{ backgroundColor: '#F7F4EF' }}>
                      <span className="text-[12px] capitalize" style={{ color: '#3C3226' }}>{src}</span>
                      <span className="text-[12px] font-medium" style={{ color: '#B8985A' }}>{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Completions */}
            {portfolio.recent_completions?.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: '#8C7E6E' }}>Recent Activity</p>
                <div className="space-y-1">
                  {portfolio.recent_completions.slice(0, 5).map(c => (
                    <div key={c.action_id} className="flex items-center gap-2 p-2" style={{ backgroundColor: '#F7F4EF' }}>
                      <Check size={10} style={{ color: '#1D6B4F' }} />
                      <span className="text-[11px] flex-1 truncate" style={{ color: '#3C3226' }}>{c.title}</span>
                      <span className="text-[10px]" style={{ color: '#8C7E6E' }}>{c.completed_at?.slice(0, 10)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="p-8 text-center">
            <p className="text-sm" style={{ color: '#8C7E6E' }}>Complete your first task to start building your portfolio.</p>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
